// @flow
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {
  Dialog,
  Hidden,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import CustomButton from '../../../elements/CustomButton/CustomButton';

import {
  ORDER_STATE_COLORS,
  ORDER_STATE_STRINGS,
  ORDER_STATE_CHANGE_DESCRIPTIONS,
} from '../../../entities';
import type {Order, OrderStateMessageConfig, Store} from '../../../entities';
import {Close, WhatsApp} from '@material-ui/icons';
import {colors} from '../../../assets/styles/colors';
import {Link} from 'react-router-dom';
import {generateWhatsappLink, replaceTemplate} from '../../../utils/share';

import {connect} from 'react-redux';
import CustomTextEditor from '../../../elements/CustomTextEditor/CustomTextEditor';
import {commaFormat} from '../../../utils/number';
import {getCustomDomain} from '../../../utils/component';
import {useIsFeatureInTier} from '../../../utils/component';

import type {RootState} from '../../index';
import type {TextState, TextAreaTextApi} from '@uiw/react-md-editor';
import {useStoreCurrency} from '../../currency/utils';

export default connect(
  (state: RootState) => ({
    orderStateMessages: state.salesProcessConfig.orderStateMessages,
    error: state.salesProcessConfig.error,
    store: state.store.store,
  }),
  null
)(StateChangeMessageModal);

type Props = {|
  open: boolean,
  order: Order,
  onClose: () => void,
  orderStateMessages?: OrderStateMessageConfig[],
  getOrderStateMessages: () => void,
  store?: Store,
|};

const useStyles = accentColor =>
  makeStyles(theme => ({
    root: {
      // zIndex: '1101 !important',
    },
    paper: {
      position: 'absolute',
      right: 0,
      padding: theme.spacing(3),
      alignItems: 'flex-start',
      height: '100%',
      maxHeight: '100%',
      maxWidth: 560,
      margin: 0,
      borderRadius: 0,
      [theme.breakpoints.down('sm')]: {
        borderRadius: '24px 24px 0 0',
        padding: theme.spacing(2),
        height: 'unset',
        maxHeight: 'unset',
        right: 'unset',
        bottom: 64,
      },
    },
    coloredBox: {
      position: 'relative',
      height: '100%',
      width: '100%',
      background: accentColor && accentColor.light,
      borderRadius: '16px',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    closeButton: {
      position: 'absolute',
      top: 12,
      right: 14,
    },
    statePill: {
      marginTop: theme.spacing(1),
      // width: 96,
      color: 'white',
      fontSize: '0.875rem',
      backgroundColor: accentColor && accentColor.main,
      borderRadius: '12px',
      padding: '2px 13px',
      textAlign: 'center',
    },
    title: {
      marginTop: theme.spacing(1),
      fontWeight: 600,
      fontSize: '1.375rem',
    },
    formSection: {
      marginTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      [theme.breakpoints.up('md')]: {
        height: '100%',
        background: 'white',
        borderRadius: '16px',
        padding: theme.spacing(2),
      },
    },
    descriptionText: {
      whiteSpace: 'pre-line',
      fontSize: '0.875rem',
      color: colors.grey800,
    },
    customTextEditor: {
      width: '100%',
      marginTop: theme.spacing(2),
    },
    actionButton: {
      padding: '5px 15px',
      fontSize: '12px',
      color: '#7B39FF',
      border: `1px solid ${colors.purple3}`,
      fontFamily: 'Poppins',
      fontWeight: 600,
      borderRadius: '20px',
    },
    buttonsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
    },
    sendButton: {
      marginTop: theme.spacing(2),
      height: 48,
      padding: theme.spacing(0, 5),
      fontWeight: 600,
      fontSize: '1.125rem',
      marginBottom: 'auto',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    sendTiendaChatButton: {
      height: 48,
      padding: theme.spacing(0, 5),
      fontWeight: 600,
      width: '100%',
      fontSize: '1.125rem',
      marginBottom: 'auto',
      color: 'rgba(247, 251, 240, 1)',
      background: 'rgb(84, 209, 140)',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },

      '&:hover': {
        background: 'rgba(84, 209, 140, .75)',
        color: 'rgba(247, 251, 240, 1)',
      },
    },
    tip: {
      marginTop: '.5rem',
      fontSize: '0.875rem',
    },
    tip2: {
      margin: theme.spacing(4, 4, 0, 4),
      textAlign: 'center',
      fontSize: '0.875rem',
      color: colors.grey600,
    },
  }));

const actionButtonStyle = {
  padding: '5px 15px',
  fontSize: '12px',
  color: '#7B39FF',
  border: `1px solid ${colors.purple3}`,
  fontFamily: 'Poppins',
  fontWeight: 600,
  borderRadius: '20px',
};
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const TransitionMobile = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function StateChangeMessageModal(props: Props) {
  const {open, order, onClose, orderStateMessages, store} = props;

  const accentColor = ORDER_STATE_COLORS[order.orderState];

  const currency = useStoreCurrency();

  const hasOrderStateChangeMessagesFeature = useIsFeatureInTier(
    'ORDER_STATE_CHANGE_MESSAGES'
  );

  const hasTiendaChatFeature = useIsFeatureInTier('TIENDACHAT_WHATSAPP_WEB');
  const classes = useStyles(accentColor)();
  const mobile = useMediaQuery('(max-width:960px)');

  const [text, setText] = useState('');

  const buildReplaceTextData = useCallback(() => {
    if (order && store && store.domains) {
      return {
        nombre_cliente: order.customer.firstName,
        link_de_pago:
          order.invoice.paymentMethod === 'CARD' ||
          order.invoice.paymentMethod === 'OTHERS'
            ? `${window.location.protocol}//${getCustomDomain(store)}/order/${
                order.id
              }?token=${order.token}`
            : order.invoice.paymentMethod === 'CONTINUE_TO_CHAT'
            ? `${window.location.protocol}//${getCustomDomain(store)}/order/${
                order.id
              }/payment-method?token=${order.token}`
            : '',
        monto_de_orden: order.invoice.detail.total
          ? commaFormat(order.invoice.detail.total, currency?.symbol)
          : '',
        link_de_review: order.token
          ? `${window.location.protocol}//${getCustomDomain(store)}/order/${
              order.id
            }/review?token=${order.token}`
          : '',
      };
    }
    return {};
  }, [currency, order, store]);

  useEffect(() => {
    if (orderStateMessages && order) {
      const stateMessageConfig = orderStateMessages.find(
        config => config.orderState === order.orderState
      );
      const dataToReplace = buildReplaceTextData();
      if (
        stateMessageConfig &&
        stateMessageConfig.enabled &&
        stateMessageConfig.message
      )
        setText(replaceTemplate(stateMessageConfig.message, dataToReplace));
    }
  }, [buildReplaceTextData, order, orderStateMessages]);

  const whatsappLink = useMemo(() => {
    if (order) {
      const dataToReplace = buildReplaceTextData();
      const replacedText = replaceTemplate(text, dataToReplace);
      return generateWhatsappLink(order.customer.phone, replacedText);
    }
  }, [buildReplaceTextData, order, text]);

  const tiendaChatLink = useMemo(() => {
    if (order) {
      const dataToReplace = buildReplaceTextData();
      const replacedText = replaceTemplate(text, dataToReplace);

      const phone = order.customer.phone;
      const message = encodeURIComponent(replacedText);

      const urlSearchParams = new URLSearchParams();

      urlSearchParams.append('message', message);
      urlSearchParams.append('identifier', phone);

      urlSearchParams.append('firstName', order.customer.firstName);
      urlSearchParams.append('lastName', order.customer.lastName);

      urlSearchParams.append('orderId', order.id);

      return `/chat/multi-chat?${urlSearchParams.toString()}`;
    }
  }, [buildReplaceTextData, order, text]);

  const extraCommands = useMemo(() => {
    return [
      {
        name: 'Customer name',
        enabled: true,
        keyCommand: 'nombre_cliente',
        buttonProps: {'aria-label': 'Insert customer name'},
        icon: <div style={actionButtonStyle}>Nombre de cliente</div>,
        execute: (state: TextState, api: TextAreaTextApi) => {
          api.replaceSelection(`${order.customer.firstName}`);
        },
      },
      (order.invoice.paymentMethod === 'CARD' ||
        order.invoice.paymentMethod === 'OTHERS' ||
        order.invoice.paymentMethod === 'CONTINUE_TO_CHAT') &&
      order.token &&
      store &&
      store.domains
        ? {
            name: 'Payment link',
            enabled: true,
            keyCommand: 'link_de_pago',
            buttonProps: {'aria-label': 'Insert payment link'},
            icon: <div style={actionButtonStyle}>Link de pago</div>,
            execute: (state: TextState, api: TextAreaTextApi) => {
              api.replaceSelection(
                order.invoice.paymentMethod === 'CONTINUE_TO_CHAT'
                  ? `${window.location.protocol}//${getCustomDomain(
                      store
                    )}/order/${order.id}/payment-method?token=${order.token}`
                  : `${window.location.protocol}//${getCustomDomain(
                      store
                    )}/order/${order.id}?token=${order.token}`
              );
            },
          }
        : undefined,
      order.invoice.detail.total && store && store.domains
        ? {
            name: 'Order amount',
            enabled: true,
            keyCommand: 'monto_de_orden',
            buttonProps: {'aria-label': 'Insert order amount'},
            icon: <div style={actionButtonStyle}>Monto de la orden</div>,
            execute: (state: TextState, api: TextAreaTextApi) => {
              api.replaceSelection(
                commaFormat(order.invoice.detail.total, currency?.symbol)
              );
            },
          }
        : undefined,
      order.orderState === 'DELIVERED' && order.token && store && store.domains
        ? {
            name: 'Review link',
            enabled: true,
            keyCommand: 'link_de_review',
            buttonProps: {'aria-label': 'Insert review link'},
            icon: <div style={actionButtonStyle}>Link de reseña</div>,
            execute: (state: TextState, api: TextAreaTextApi) => {
              api.replaceSelection(
                `${window.location.protocol}//${getCustomDomain(store)}/order/${
                  order.id
                }/review?token=${order.token}`
              );
            },
          }
        : undefined,
    ];
  }, [currency, order, store]);

  const formSection = (
    <div className={classes.formSection}>
      <Typography className={classes.descriptionText}>
        {ORDER_STATE_CHANGE_DESCRIPTIONS[order.orderState]}
      </Typography>
      <CustomTextEditor
        disabled={!hasOrderStateChangeMessagesFeature}
        className={classes.customTextEditor}
        text={text}
        onChange={s => setText(s)}
        extraCommands={extraCommands}
      />

      <div className={classes.buttonsContainer}>
        <CustomButton
          className={classes.sendButton}
          variant="contained"
          startIcon={<WhatsApp />}
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          Enviar
        </CustomButton>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: '8px',
          }}
        >
          O continúa en TiendaChat
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <CustomButton
            className={classes.sendTiendaChatButton}
            to={tiendaChatLink}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!hasTiendaChatFeature}
          >
            Enviar desde tiendachat
          </CustomButton>
          {!hasTiendaChatFeature && (
            <Typography className={classes.tip}>
              Mejora tu plan para enviar mensajes desde TiendaChat.{' '}
              <Link to="/tiers-list" target="_blank">
                Ver planes
              </Link>
            </Typography>
          )}
        </div>

        <Typography className={classes.descriptionText}>
          ¿Quieres configurar el mensaje para todos?{' '}
          <Link to="/configure/sales-process" target="_blank">
            Clic Aquí.
          </Link>
        </Typography>
      </div>
    </div>
  );

  return (
    <Dialog
      className={classes.root}
      // fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={mobile ? TransitionMobile : Transition}
      PaperProps={{className: classes.paper}}
    >
      <div className={classes.coloredBox}>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <Close />
        </IconButton>
        <div className={classes.statePill}>
          {ORDER_STATE_STRINGS[order.orderState]}
        </div>
        <Typography className={classes.title}>Enviar un mensaje</Typography>
        <Hidden smDown>{formSection}</Hidden>
      </div>
      <Hidden mdUp>{formSection}</Hidden>
      <Hidden smDown>
        <Typography className={classes.tip2}>
          *Recuerda que debes tener WhatsApp configurado en este dispositivo
          para poder enviar los mensajes.
        </Typography>
      </Hidden>
    </Dialog>
  );
}

StateChangeMessageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  order: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  orderStateMessages: PropTypes.array,
};
